import React, { useEffect, useState } from "react";
import CompanyDetailPresentational from "./CompanyDetailPresentational";
import "./CompanyDetail.scss";
import {
  fetchCompany,
  saveCompany,
  deleteCompany,
} from "../../lib/fetch/company";
import { useTranslation } from "react-i18next";
import { useAlert } from "react-alert";
import { useAuth } from "../../components/Auth";
import { useLoading } from "../../components/Loading";
import { fetchContacts, saveContact, createContact } from "../../lib/fetch/contacts";
import fetchDeals from "../../lib/fetch/deals";
import { useQueryParams, NumberParam } from "use-query-params";

function CompanyDetail(props) {
  const { match, history } = props;
  const { params: { id } = {} } = match;
  const [company, setCompany] = useState({});
  const [contacts, setContacts] = useState([]);
  const [t] = useTranslation("COMPANYDETAIL");
  const [{ token } = {}] = useAuth();
  const alert = useAlert();
  const { showLoading, hideLoading } = useLoading();

  const [isLoading, setIsLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [deals, setDeals] = useState([]);
  const [kpis, setKpis] = useState({});
  const [saveStatus, setSaveStatus] = useState("idle"); // idle, saving, success, error
  const [saveMessage, setSaveMessage] = useState({
    errorMessage: "",
    successMessage: "",
  });

  const [query, setQuery] = useQueryParams({ offset: NumberParam });
  const { offset = 0 } = query;

  useEffect(() => {
    const fetch = async (id) => {
      const { error, data } = await fetchCompany({ id, token });

      if (error) {
        alert.error(t("COMMON:GENERIC_ERROR"));
        return;
      }
      setCompany(data);

      const { error: errorContacts, data: dataContacts } = await fetchContacts({
        companyId: id,
        token,
        limit: 1000000,
      });
      if (!errorContacts) {
        setContacts(dataContacts);
      }
    };

    Number(id) && fetch(id);
  }, [alert, id, t, token]);

  useEffect(() => {
    const fetch = async (query) => {
      setIsLoading(true);
      const { error, data, headers } = await fetchDeals(query);

      if (error) {
        setIsLoading(false);
        alert.error(t("COMMON:GENERIC_ERROR"));
        return;
      }

      setIsLoading(false);
      setDeals(data.deals);
      setKpis(data.kpis);
      setTotal(Number(headers["x-total-count"]));
    };

    fetch({ token, offset, companyId: id });
  }, [alert, id, t, token, offset]);

  const onAddContact = async (values) => {
    const { error, data } = await saveContact({
      token,
      id: values.contact_id,
      data: { company_id: company.id },
    });
    if (error) {
      alert.error(t("COMMON:GENERIC_ERROR"));
      return;
    }
    setContacts([...contacts?.filter((c) => c.id !== data.id), data]);
  };

  const onCreateContact = async () => {
    const defaultContact = {
      first_name: t('COMMON:NEW_CONTACT_FIRSTNAME'),
      last_name: t('COMMON:NEW_CONTACT_LASTNAME'),
      company_id: company.id,
    };

    const { error, data } = await createContact({ data: defaultContact, token });

    if (error) {
      alert.error(t('COMMON:GENERIC_ERROR'));
      return;
    }

    history.push(`/contact-detail/${data.id}`);
  };

  const onSave = async (values) => {
    setSaveStatus("saving");
    const { error, data } = await saveCompany({ token, id, data: values });
    if (error) {
      setSaveStatus("error");
      if (error.response?.status === 409) {
        setSaveMessage({
          errorMessage: t(error.response.data?.message),
          successMessage: ""
        });
      }
      return;
    }
    setCompany(data);
    setSaveStatus("success");
  };

  const onDelete = async () => {
    showLoading();
    const { error } = await deleteCompany({ token, id });
    hideLoading();

    if (error) {
      alert.error(t("COMMON:GENERIC_ERROR"));
      return;
    }

    history.push("/companies");
    alert.success(t("COMMON:DELETE_SUCCESS"));
  };

  return (
    <CompanyDetailPresentational
      company={company}
      contacts={contacts}
      deals={deals}
      kpis={kpis}
      onSave={onSave}
      onAddContact={onAddContact}
      onCreateContact={onCreateContact}
      onDelete={onDelete}
      isLoading={isLoading}
      total={total}
      onFilter={setQuery}
      query={query}
      saveStatus={saveStatus}
      saveMessage={saveMessage}
      onSaveStatusChange={setSaveStatus}
      {...props}
    />
  );
}
export default CompanyDetail;
